import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { filter, isEmpty } from 'lodash';
import { withRouter } from 'react-router-dom';
import { reset as resetForm } from 'redux-form';

import { MODAL_TYPE_PAYMENT_REQUEST_EBON_UNAVAILABLE } from '../../../../../../services/modal/constants';
import { PAYMENT_REQUEST_TYPE_EBON } from '../../../../../../services/account/paymentrequest/request/constants';

import { showModal } from '../../../../../../services/modal/actions';
import {
    sendPaymentRequest,
    reset,
    setTimeoutId
} from '../../../../../../services/account/paymentrequest/request/actions';
import { createOrder, resetCart, resetLock } from '../../../../../../services/ebon/actions';

import { truncateAmount } from '../../../../../../utils';
import { mergeQueryLocked } from '../../../../../../utils/ebonUtils';
import { convertHtmlToReactDomWithParams } from '../../../../../../utils/htmlUtils';

import { getValidatedAmount } from '../../../../../../services/account/gains/selector';
import { getOnGoingPaidAmount } from '../../../../../../services/account/paymentrequest/ongoing/selector';

import PaymentRequestEbonPartnerForm from './PaymentRequestEbonPartnerForm';
import PaymentRequestEbonFormComponent from './PaymentRequestEbonFormComponent';
import PaymentRequestEbonSelectorComponent from './ebonselector/PaymentRequestEbonSelectorComponent';

import './PaymentRequestEbonComponent.scss';

const FORM_NAME = 'paymentrequest-form';

const mapUserCart = (cart, partners) => {
    if (cart.length > 0) {
        const partner = filter(partners, p => p.id === cart[0].partnerId)[0];
        return cart.map(item => {
            return {
                quantity: item.quantityQuery,
                partnerName: partner.name,
                value: item.facialValue
            };
        });
    }
    return [];
};

const PaymentRequestEbonComponent = ({
    i18n,
    validatedMinusPaymentRequest,
    ebons,
    ebonPartners,
    ebonCart,
    userCart,
    ebonLock,
    lockFailure,
    createdOrder,
    sendPaymentRequest,
    resetForm,
    createOrder,
    resetCart,
    showModal,
    reset,
    setTimeoutId,
    resetLock
}) => {
    const [selectedPartnerId, setSelectedPartnerId] = useState(-1);
    const [email, setEmail] = useState('');
    const [requestedAmount, setRequestedAmount] = useState(0);
    const [cappingError, setCappingError] = useState(false);

    const hasFreeFacial = ebons.freeFacialValue !== undefined;

    const cartAmount = userCart.reduce(
        (previousValue, currentValue) => previousValue + currentValue.quantity * currentValue.value,
        0
    );
    const remainingJackpotAfterPayment =
        validatedMinusPaymentRequest - cartAmount < 0
            ? 0
            : validatedMinusPaymentRequest - cartAmount;

    useEffect(() => {
        scrollTop();
    }, []);

    useEffect(() => {
        if (!isEmpty(ebonLock)) {
            const { allLocked } = ebonLock;
            if (!allLocked) {
                delete ebonLock.allLocked;
                const final = mergeQueryLocked(ebonCart, ebonLock);
                showModal(MODAL_TYPE_PAYMENT_REQUEST_EBON_UNAVAILABLE, {
                    data: final,
                    partner: getEbonPartner()
                });
            } else {
                createOrder(selectedPartnerId, null, null, !hasFreeFacial);
            }
        }
    }, [ebonLock]);

    useEffect(() => {
        if (lockFailure && lockFailure.statusCode === 406) {
            setCappingError(true);
        }
    }, [lockFailure]);

    useEffect(() => {
        if (
            createdOrder &&
            requestedAmount > 0 &&
            requestedAmount <= validatedMinusPaymentRequest
        ) {
            const body = {
                email: email,
                requestedAmount: requestedAmount,
                orderId: createdOrder.id
            };
            sendPaymentRequest(PAYMENT_REQUEST_TYPE_EBON, body, () => {
                const id = setTimeout(() => {
                    reset();
                }, 20000);
                setTimeoutId(id);

                resetCart();
                resetLock();
                resetForm(FORM_NAME);
                scrollTop();
            });
        }
    }, [createdOrder]);

    const scrollTop = () => {
        if (document.getElementById('paymentAreaAnchor')) {
            document.getElementById('paymentAreaAnchor').scrollIntoView({ behavior: 'smooth' });
        }
    };

    const getEbonPartner = () => {
        const ebonPartner = ebonPartners.filter(p => p.id === selectedPartnerId);
        return ebonPartner.length > 0 ? ebonPartner[0] : null;
    };

    return (
        <div id="payment-request-ebon">
            <PaymentRequestEbonPartnerForm
                setSelectedPartnerId={setSelectedPartnerId}
                setCappingError={setCappingError}
            />

            {selectedPartnerId && getEbonPartner() && (
                <React.Fragment>
                    <PaymentRequestEbonSelectorComponent
                        partner={getEbonPartner()}
                        validatedCashbackAmount={validatedMinusPaymentRequest}
                    />

                    <p className="payment-remaining-indication">
                        {convertHtmlToReactDomWithParams(
                            i18n.PAYMENT_REQUEST_FORM.EBON
                                .REMAINING_JACKPOT_AFTER_EBON_PAYMENT_INDICATION,
                            getEbonPartner().name,
                            cartAmount.toFixed(2).replace('.', ','),
                            remainingJackpotAfterPayment.toFixed(2).replace('.', ',')
                        )}
                    </p>

                    <PaymentRequestEbonFormComponent
                        selectedPartnerId={selectedPartnerId}
                        partner={getEbonPartner()}
                        cartAmount={cartAmount}
                        setRequestedAmount={setRequestedAmount}
                        ebonCart={ebonCart}
                        cappingError={cappingError}
                        remainingJackpotAfterPayment={remainingJackpotAfterPayment}
                        setEmail={setEmail}
                        formName={FORM_NAME}
                    />
                </React.Fragment>
            )}
        </div>
    );
};

const mapDispatchToProps = {
    sendPaymentRequest,
    resetForm,
    createOrder,
    resetCart,
    showModal,
    reset,
    setTimeoutId,
    resetLock
};

const mapStoreToProps = state => {
    const validatedCashbackAmount = getValidatedAmount(state);
    const paymentRequestTotalAmount = getOnGoingPaidAmount(state);
    const validatedMinusPaymentRequest = truncateAmount(
        validatedCashbackAmount - paymentRequestTotalAmount
    );

    return {
        i18n: state.i18n.page.ACCOUNT.CASHBACK_JACKPOT,
        validatedMinusPaymentRequest: validatedMinusPaymentRequest,
        ebons: state.ebon.ebons,
        ebonPartners: state.ebon.ebonPartners,
        ebonCart: state.ebon.cart,
        userCart: mapUserCart(state.ebon.cart, state.ebon.ebonPartners),
        ebonLock: state.ebon.lock,
        lockFailure: state.ebon.lockFailure,
        createdOrder: state.ebon.createdOrder
    };
};

export default withRouter(
    connect(mapStoreToProps, mapDispatchToProps)(PaymentRequestEbonComponent)
);



// WEBPACK FOOTER //
// ./src/components/pages/account/cashback/payment-requests/ebon/PaymentRequestEbonComponent.js